<template>
    <div class="mt-3 flex flex-col gap-3">
        <!-- Header / Controls -->
        <div class="flex flex-col gap-2 no-print">
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
                <div v-if="!isTertiary" class="xl:col-span-2">
                    <label class="block text-[11px] font-medium text-gray-500 mb-1">Type</label>
                    <Select v-model="selectedType" :options="typeOptions" optionLabel="label" optionValue="value"
                        size="small" class="w-full rounded-lg" />
                </div>
                <div class="xl:col-span-2">
                    <label class="block text-[11px] font-medium text-gray-500 mb-1">Select {{ selectedTypeLabel }}</label>
                    <Select v-model="selectedTimeableId" :options="timeableOptions" optionLabel="label"
                        optionValue="value" placeholder="Choose a target..." size="small" class="w-full rounded-lg" filter />
                </div>
            </div>

            <div v-if="selectedTimeableId && timetable" class="flex gap-1.5 w-full md:w-auto">
                <Button v-if="can(currentUser, AppPermissions.TimeTable.Actions.Create, AppPermissions.TimeTable.Resource)"
                    label="Copy From" icon="pi pi-copy" size="small" class="p-button-outlined p-button-secondary flex-1 md:flex-none"
                    @click="isCopyDialogOpen = true" />
                <Button label="Print" icon="pi pi-print" size="small" class="p-button-outlined p-button-secondary flex-1 md:flex-none"
                    @click="printTimetable" />
            </div>
        </div>

        <!-- Main Content -->
        <div v-if="!selectedTimeableId"
            class="text-center py-12 bg-gray-50 rounded-xl border-dashed border-2 border-gray-200">
            <i class="pi pi-calendar text-2xl text-gray-300 mb-2 block"></i>
            <p class="text-gray-500 text-sm font-medium">{{ selectPrompt }}
            </p>
        </div>

        <div v-else-if="loadingTimetable" class="flex justify-center py-12">
            <i class="pi pi-spin pi-spinner text-2xl text-primary"></i>
        </div>

        <div v-else-if="!timetable" class="text-center py-12 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div class="max-w-md mx-auto">
                <h3 class="text-base font-bold text-gray-800 mb-1.5">No Timetable Found</h3>
                <p class="text-gray-500 text-sm mb-4">This item doesn't have a timetable initialized yet. Select a template to
                    get started.</p>

                <div class="flex flex-col gap-2 items-center">
                    <Select v-model="selectedTemplateId" :options="templateOptions" optionLabel="name"
                        optionValue="id" placeholder="Select a Bell Schedule Template" class="w-full" />
                    <Button v-if="can(currentUser, AppPermissions.TimeTable.Actions.Create, AppPermissions.TimeTable.Resource)"
                        label="Initialize Timetable" icon="pi pi-save" size="small"
                        class="p-button-primary !bg-primary !border-primary w-full" :disabled="!selectedTemplateId"
                        :loading="creating" @click="initializeTimetable" />
                </div>
            </div>
        </div>

        <div v-else>
            <div class="flex flex-wrap items-center gap-1 mb-2 no-print">
                <span class="text-xs font-medium text-gray-600">Days:</span>
                <div v-for="day in allDays" :key="day"
                    class="px-2 py-0.5 text-[10px] rounded-full cursor-pointer transition-all border"
                    :class="activeDays.includes(day)
                        ? 'bg-primary/10 border-primary text-primary font-medium'
                        : 'bg-gray-50 border-gray-200 text-gray-400'"
                    @click="toggleDay(day)">
                    {{ day.slice(0, 3) }}
                </div>
            </div>

            <h1 class="hidden print:block text-xl font-bold mb-3 text-center">Timetable: {{ selectedTimeableName }}</h1>
            <TimetableGrid :slots="(sortedSlots as TimetableTimeSlot[])" :entries="(timetable.entries as TimetableEntry[])" :days="activeDays"
                :editable="can(currentUser, AppPermissions.TimeTable.Actions.Update, AppPermissions.TimeTable.Resource)"
                @cell-click="openEntryEditor" />
        </div>

        <!-- Entry Editor Modal -->
        <EntryEditor v-bind="{ slot: editingSlot }" :visible="isEditorOpen" :day="editingDay" :entry="editingEntry"
            :timetableId="timetable?.id ?? ''" :currentEntry="editingEntry" :classroomId="selectedTimeableId ?? undefined"
            @close="isEditorOpen = false" @saved="refreshTimetable" @deleted="refreshTimetable" />

        <!-- Copy Timetable Dialog -->
        <CopyTimetableDialog v-if="timetable" :visible="isCopyDialogOpen"
            :currentTimetableId="timetable.id"
            :currentTemplateId="timetable.template?.id || ''"
            :existingEntries="(timetable.entries as TimetableEntry[]) || []"
            @close="isCopyDialogOpen = false" @copied="refreshTimetable" />
    </div>
</template>

<script setup lang="ts">
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import {
  useGetTimetablesQuery,
  useGetTimetableTemplatesQuery,
  useCreateTimetableMutation,
} from '@/types/vue-apollo-helpers';
import { TimetableTimeableType } from '@/types/composition-functions';
import type { TimetableTimeSlot, TimetableEntry } from '@/types/composition-functions';
import TimetableGrid from './TimetableGrid.vue';
import EntryEditor from './EntryEditor.vue';
import CopyTimetableDialog from './CopyTimetableDialog.vue';
import { useToast } from 'primevue/usetoast';
import { can, AppPermissions } from '@/utils/permissions';
import { useUserStore } from '@/store/userStore';
import { ALL_DAYS } from '@/constants/timetable-presets';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { defaultTimetableTitle } from '@/utils/timetableLabel';

const userStore = useUserStore();
const currentUser = computed(() => userStore.currentUser);
const { isTertiary } = useSchoolFeatures();
const { labels } = useSchoolLabels();

// State
const selectedType = ref<TimetableTimeableType>(TimetableTimeableType.Classroom);
const selectedTimeableId = ref(null);
const selectedTemplateId = ref(null);
const isEditorOpen = ref(false);
const isCopyDialogOpen = ref(false);
const editingSlot = ref<TimetableTimeSlot | null>(null);
const editingDay = ref('');
const editingEntry = ref<TimetableEntry | null>(null);
const allDays = [...ALL_DAYS];
const activeDays = ref<string[]>(['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY']);

function toggleDay(day: string) {
  const idx = activeDays.value.indexOf(day);
  if (idx >= 0 && activeDays.value.length > 1) {
    activeDays.value.splice(idx, 1);
  } else if (idx < 0) {
    const sorted = allDays.filter(d => [...activeDays.value, day].includes(d));
    activeDays.value = sorted;
  }
}

// Queries
const { classrooms, classroomSections } = useSchoolQuery();
const { result: templateResult } = useGetTimetableTemplatesQuery();

// Timetable Query (Lazy interaction via ref variable, or responsive query)
const { result: timetableResult, loading: loadingTimetable, refetch: refetchTimetable } = useGetTimetablesQuery(
  () => ({ timeable_id: selectedTimeableId.value, timeable_type: selectedType.value }),
  { enabled: computed(() => !!selectedTimeableId.value) },
);

const { mutate: createTimetable, loading: creating } = useCreateTimetableMutation();

// Computed
// Tertiary schools schedule by level only, so the type picker is hidden and
// everything stays on the Classroom lens (a classroom IS the level there).
const typeOptions = computed(() => [
  { label: labels.value.CLASSROOM, value: TimetableTimeableType.Classroom },
  { label: labels.value.CLASSROOM_SECTION, value: TimetableTimeableType.ClassroomSection },
]);

const selectedTypeLabel = computed(() =>
  selectedType.value === TimetableTimeableType.Classroom
    ? labels.value.CLASSROOM
    : labels.value.CLASSROOM_SECTION,
);

const selectPrompt = computed(() =>
  isTertiary.value
    ? 'Please select a level to view or manage its timetable.'
    : 'Please select a classroom or section to view or manage its timetable.',
);

const timeableOptions = computed(() => {
  if (selectedType.value === TimetableTimeableType.Classroom) {
    return classrooms.value
      .filter((c): c is NonNullable<typeof c> => c !== null)
      .map((c) => ({ label: c.name, value: c.id }));
  } else {
    return classroomSections.value
      .filter((s): s is NonNullable<typeof s> => s !== null)
      .map((s) => {
        const sectionName = s.name || s.section?.name;
        return {
          label: sectionName ? `${s.classroom.name} - ${sectionName}` : s.classroom.name,
          value: s.id,
        };
      });
  }
});

const selectedTimeableName = computed(() => timeableOptions.value.find((c) => c.value === selectedTimeableId.value)?.label || '');
const templateOptions = computed(() => templateResult.value?.timetableTemplates || []);
// GET_TIMETABLES returns a list, we take the first one since we filter by ID
const timetable = computed(() => timetableResult.value?.timetables?.data?.[0] || null);

const sortedSlots = computed(() => {
  if (!timetable.value?.template?.timeSlots) return [];
  return [...timetable.value.template.timeSlots].sort((a, b) => a.start_time.localeCompare(b.start_time));
});

// Watch type change to reset selection
watch(selectedType, () => {
  selectedTimeableId.value = null;
});

// Implementation
async function initializeTimetable() {
  if (!selectedTimeableId.value || !selectedTemplateId.value) return;
  try {
    await createTimetable({
      input: {
        timeable_id: selectedTimeableId.value,
        timeable_type: selectedType.value,
        timetable_template_id: selectedTemplateId.value,
        title: defaultTimetableTitle(selectedTimeableName.value, selectedType.value),
      },
    });
    await refetchTimetable();
  } catch {
    // mutation error is surfaced via Apollo error state; no action needed here
  }
}

function openEntryEditor({ day, slot, entry }: { day: string; slot: TimetableTimeSlot; entry?: TimetableEntry }) {
  editingDay.value = day;
  editingSlot.value = slot;
  editingEntry.value = entry ?? null;
  isEditorOpen.value = true;
}

async function refreshTimetable() {
  await refetchTimetable();
}

function printTimetable() {
  window.print();
}

</script>

<style>
@media print {

    /* Hide all other elements including global layout parts */
    .no-print,
    header,
    nav,
    aside,
    .layout-topbar,
    .layout-sidebar,
    .p-tabview-nav,
    .p-tabview-nav-container,
    .card-header {
        display: none !important;
    }

    /* Reset margins for full page print */
    body,
    #app,
    main,
    .layout-main,
    .card {
        margin: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
        width: 100% !important;
        box-shadow: none !important;
        border: none !important;
        background: white !important;
    }

    /* Ensure grid is visible */
    .timetable-grid {
        width: 100% !important;
        display: block !important;
    }

    /* Helper utilities for print */
    .print\:block {
        display: block !important;
    }

    .print\:hidden {
        display: none !important;
    }

    /* Page settings */
    @page {
        margin: 0.5cm;
        size: landscape;
    }
}
</style>
